import type { Variant } from "@app/protocol";
import { useT } from "../i18n/context";
import { RulesText } from "./RulesLink";

/** Games on offer, with their targets and the suit on their card (design). */
export const GAMES: readonly {
  variant: Variant;
  goals: readonly number[];
  suit: string;
  tone: string;
}[] = [
  { variant: "batak", goals: [21, 35, 51], suit: "♠", tone: "text-ink" },
  { variant: "spades", goals: [200, 300, 500], suit: "♠", tone: "text-suit-steel" },
  { variant: "tarneeb", goals: [31, 41, 61], suit: "♥", tone: "text-suit-red" },
  { variant: "baloot", goals: [152], suit: "♦", tone: "text-suit-red" },
  { variant: "hearts", goals: [50, 100], suit: "♥", tone: "text-suit-red" },
  // King plays a number of hands, never to a score: its lengths come from the rules.
  { variant: "king", goals: [], suit: "♣", tone: "text-ink" },
  { variant: "pisti", goals: [51, 101, 151], suit: "♦", tone: "text-suit-red" },
];

type Game = (typeof GAMES)[number];

/** A game's tile (owner's design, 17 Sept 2026): the suit in a box, the name, a few words on what it is. */
export function GameCard({
  game,
  selected,
  onClick,
}: {
  game: Game;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useT();
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={t(`variant.${game.variant}`)}
      className={`flex min-w-0 items-center gap-3 rounded-[16px] border-[1.5px] bg-panel-strong p-2.5 text-start backdrop-blur-[10px] ${selected ? "border-gold shadow-glow-soft" : "border-ink/18 hover:border-ink/40"}`}
      onClick={onClick}
    >
      <span
        aria-hidden="true"
        className={`flex h-11 w-11 flex-none items-center justify-center rounded-[12px] text-[22px] leading-none ${selected ? "bg-gold text-gold-ink" : `bg-ink/8 ${game.tone}`}`}
      >
        {game.suit}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[16px] font-extrabold">{t(`variant.${game.variant}`)}</span>
        <span className="text-[12px] leading-snug text-ink/70">
          {t(`variant.${game.variant}.tag`)}
        </span>
      </span>
    </button>
  );
}

/** The settings panel's head: the chosen game in a sentence, and the way to its rules. */
export function SettingsHead({ variant }: { variant: Variant }) {
  const { t } = useT();
  const game = GAMES.find((entry) => entry.variant === variant);
  if (game === undefined) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4">
      <span aria-hidden="true" className={`flex-none text-[28px] leading-none ${game.tone}`}>
        {game.suit}
      </span>
      <div className="flex min-w-0 flex-1 basis-[220px] flex-col gap-0.5">
        <h3 className="flex flex-wrap items-baseline gap-x-3">
          <span className="text-[19px] font-extrabold">{t(`variant.${game.variant}`)}</span>
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink/60">
            {t("home.settings")}
          </span>
        </h3>
        <p className="text-[13px] text-ink/75">{t(`variant.${game.variant}.blurb`)}</p>
      </div>
      <RulesText variant={game.variant} />
    </div>
  );
}
